import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Form, Button, Card, Modal } from 'react-bootstrap';
import { PageHeader, Loading } from '../../components';
import useComponentState from '../../utils/useComponentState';
import styles from './AddRewardRule.module.scss';
import { toast } from 'react-toastify';
import {
  INITIAL_STATE,
  TOAST_MESSAGES,
  CONDITION_TYPES,
  ACTION_TYPES,
  CONDITION_OPERATORS,
  VALIDATION_ERROR_MESSAGES,
  PREDEFINED_RULES,
  CATEGORY_COLORS,
  PREDEFINED_RULE_DEFAULT_DESCRIPTION,
  PREDEFINED_RULE_DEFAULT_NAME,
} from './AddRewardRuleConstants';
import { createRewardRule, getAllRewardRules } from '../../api/rewardRule';
import {getAllTiers} from '../../api/events';

const formatDateTime = (dt) => {
  if (!dt) return null;
  return dt.length === 16 ? `${dt}:00` : dt;
};

const parseMccCodes = (mccStr) => {
  if (!mccStr || !mccStr.trim()) return [];
  return mccStr.split(',').map((s) => s.trim()).filter(Boolean);
};

const validateForm = (state) => {
  const errors = {};
  const conditionErrors = [];

  if (!state.name || !state.name.trim()) {
    errors.name = VALIDATION_ERROR_MESSAGES.name;
  }

  const priority = parseInt(state.priority, 10);
  if (!state.priority || isNaN(priority) || priority < 1) {
    errors.priority = VALIDATION_ERROR_MESSAGES.priority;
  }

  if (!state.conditionOperator) {
    errors.conditionOperator = VALIDATION_ERROR_MESSAGES.conditionOperator;
  }

  if (!state.actionType) {
    errors.actionType = VALIDATION_ERROR_MESSAGES.actionType;
  }

  const actionTypeDef = ACTION_TYPES.find((a) => a.id === state.actionType);
  if (actionTypeDef?.needsValue && (state.actionValue === '' || state.actionValue === undefined)) {
    errors.actionValue = VALIDATION_ERROR_MESSAGES.actionValue;
  }

  state.conditions.forEach((cond) => {
    const condErr = {};
    if (!cond.conditionType) {
      condErr.conditionType = VALIDATION_ERROR_MESSAGES.conditionType;
    }
    if (cond.value === '' || cond.value === undefined) {
      condErr.value = VALIDATION_ERROR_MESSAGES.conditionValue;
    }
    const condTypeDef = CONDITION_TYPES.find((c) => c.id === cond.conditionType);
    if (condTypeDef?.needsSecondary) {
      const v = parseFloat(cond.value);
      const sv = parseFloat(cond.secondaryValue);
      if (cond.secondaryValue === '' || isNaN(sv)) {
        condErr.secondaryValue = 'Secondary value is required.';
      } else if (!isNaN(v) && sv <= v) {
        condErr.secondaryValue = 'Secondary value must be greater than the first value.';
      }
    }
    conditionErrors.push(condErr);
  });

  if (state.startDate && state.endDate) {
    if (new Date(state.endDate) <= new Date(state.startDate)) {
      errors.dateRange = VALIDATION_ERROR_MESSAGES.dateRange;
    }
  }

  const hasConditionErrors = conditionErrors.some((e) => Object.keys(e).length > 0);

  return {
    isValid: Object.keys(errors).length === 0 && !hasConditionErrors,
    errors,
    conditionErrors,
  };
};

const createPayload = (state) => {
  const payload = {
    name: state.name.trim(),
    description: state.description ? state.description.trim() : null,
    isActive: state.isActive,
    priority: parseInt(state.priority, 10),
    conditionOperator: state.conditionOperator,
    conditions: state.conditions.map((c) => ({
      conditionType: c.conditionType,
      value: parseFloat(c.value),
      secondaryValue: c.secondaryValue !== '' ? parseFloat(c.secondaryValue) : null,
    })),
    action: {
      actionType: state.actionType,
      actionValue: state.actionValue !== '' ? parseFloat(state.actionValue) : null,
    },
    mccCodes: parseMccCodes(state.mccCodes),
    tierIds: state.selectedTierIds,
  };
  if (state.startDate) payload.startDate = formatDateTime(state.startDate);
  if (state.endDate) payload.endDate = formatDateTime(state.endDate);
  return payload;
};

const AddRewardRule = ({ auth }) => {
  const authToken = auth?.auth?.token;
  const [state, setState] = useComponentState(INITIAL_STATE);
  const [showTemplates, setShowTemplates] = React.useState(false);
  const [fromTemplate, setFromTemplate] = React.useState(false);
  const modalContentRef = React.useRef(null);

  React.useEffect(() => {
    if (!showTemplates) return;
    const handleMouseDown = (e) => {
      if (modalContentRef.current && !modalContentRef.current.contains(e.target)) {
        setShowTemplates(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [showTemplates]);

  useEffect(() => {
    loadTiers();
    loadExistingRules();
    // eslint-disable-next-line
  }, []);

  const loadTiers = async () => {
    setState({ tiersLoading: true });
    try {
      const data = await getAllTiers(0, authToken);
      setState({ tiers: Array.isArray(data) ? data : [], tiersLoading: false });
    } catch {
      setState({ tiers: [], tiersLoading: false });
    }
  };

  const loadExistingRules = async () => {
    try {
      const data = await getAllRewardRules(authToken);
      setState({ existingRules: Array.isArray(data) ? data : [] });
    } catch {
      setState({ existingRules: [] });
    }
  };

  const handleSelectPredefinedRule = (rule) => {
    const resolvedTierIds = rule.selectedTierNames
      ? state.tiers
          .filter((t) => rule.selectedTierNames.some((name) => t.name?.toLowerCase() === name.toLowerCase()))
          .map((t) => t.id)
      : rule.formData.selectedTierIds;

    setState({
      ...INITIAL_STATE,
      ...rule.formData,
      selectedTierIds: resolvedTierIds,
      fieldErrors: {},
      conditionErrors: [],
      loading: false,
      tiers: state.tiers,
      tiersLoading: state.tiersLoading,
      existingRules: state.existingRules,
    });
    setFromTemplate(true);
    setShowTemplates(false);
  };

  const handleTemplateFocus = (e) => {
    const { name, value } = e.target;
    if (name === 'name' && value === PREDEFINED_RULE_DEFAULT_NAME) {
      setState((prev) => ({ ...prev, name: '' }));
    } else if (name === 'description' && value === PREDEFINED_RULE_DEFAULT_DESCRIPTION) {
      setState((prev) => ({ ...prev, description: '' }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setState((prev) => ({
      ...prev,
      [name]: newValue,
      fieldErrors: { ...prev.fieldErrors, [name]: '' },
    }));
  };

  const handleConditionChange = (idx, field, value) => {
    const updatedConditions = state.conditions.map((c, i) =>
      i === idx
        ? { ...c, [field]: value, ...(field === 'conditionType' ? { value: '', secondaryValue: '' } : {}) }
        : c
    );
    const updatedErrors = [...(state.conditionErrors || [])];
    if (updatedErrors[idx]) {
      updatedErrors[idx] = { ...updatedErrors[idx], [field]: '' };
    }
    setState({ conditions: updatedConditions, conditionErrors: updatedErrors });
  };

  const handleAddCondition = () => {
    setState({
      conditions: [...state.conditions, { conditionType: '', value: '', secondaryValue: '' }],
    });
  };

  const handleRemoveCondition = (idx) => {
    if (state.conditions.length === 1) return;
    setState({
      conditions: state.conditions.filter((_, i) => i !== idx),
      conditionErrors: (state.conditionErrors || []).filter((_, i) => i !== idx),
    });
  };

  const handleTierToggle = (tierId) => {
    const selected = state.selectedTierIds;
    const updated = selected.includes(tierId)
      ? selected.filter((id) => id !== tierId)
      : [...selected, tierId];
    setState({ selectedTierIds: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { isValid, errors, conditionErrors } = validateForm(state);
    if (!isValid) {
      setState({ fieldErrors: errors, conditionErrors });
      return;
    }

    if (fromTemplate) {
      if (state.name === PREDEFINED_RULE_DEFAULT_NAME && state.description === PREDEFINED_RULE_DEFAULT_DESCRIPTION) {
        toast.info('Please update both the rule name and description before saving. The current values are template placeholders.');
        return;
      }
      if (state.name === PREDEFINED_RULE_DEFAULT_NAME) {
        toast.info('Please update the rule name before saving. The current value is a template placeholder.');
        return;
      }
      if (state.description === PREDEFINED_RULE_DEFAULT_DESCRIPTION) {
        toast.info('Please update the description before saving. The current value is a template placeholder.');
        return;
      }
    }

    const isDuplicate = (state.existingRules || []).some(
      (r) => r.name?.trim().toLowerCase() === state.name.trim().toLowerCase()
    );
    if (isDuplicate) {
      toast.info(`A rule named "${state.name.trim()}" already exists. Rename or adjust the rule before adding.`);
      return;
    }

    setState({ loading: true, fieldErrors: {}, conditionErrors: [] });
    try {
      const payload = createPayload(state);
      await createRewardRule(payload, auth.auth.token);
      toast.success(TOAST_MESSAGES.success);
      const updatedRules = [...(state.existingRules || []), { name: state.name.trim() }];
      setState({ ...INITIAL_STATE, tiers: state.tiers, existingRules: updatedRules });
      setFromTemplate(false);
    } catch (err) {
      const msg = err?.message || TOAST_MESSAGES.error;
      toast.error(msg);
      setState({ loading: false });
    }
  };

  const handleCancel = () => {
    setState({ ...INITIAL_STATE, tiers: state.tiers, existingRules: state.existingRules });
    setFromTemplate(false);
  };

  const actionTypeDef = ACTION_TYPES.find((a) => a.id === state.actionType);

  return (
    <div className={styles.root_div}>
      <PageHeader text="Add Reward Rule" />

      {/* Predefined Rule Templates Modal */}
      <Modal
        show={showTemplates}
        onHide={() => setShowTemplates(false)}
        size="xl"
        centered
        className={styles.predefineModal}
        backdrop
      >
        <div ref={modalContentRef} style={{ display: 'contents' }}>
        <Modal.Header className={styles.modalHeader}>
          <div className={styles.modalTitleGroup}>
            <Modal.Title className={styles.modalTitle}>Predefined Rule Templates</Modal.Title>
            <p className={styles.modalSubtitle}>
              Select a template to pre fill the form. You can customise all values before saving.
            </p>
          </div>
          <button
            type="button"
            className={`btn-close ${styles.modalCloseBtn}`}
            onClick={() => setShowTemplates(false)}
            aria-label="Close"
          />
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className={styles.predefinedGrid}>
            {PREDEFINED_RULES.map((rule) => (
              <div
                key={rule.id}
                className={styles.predefinedItem}
                onClick={() => handleSelectPredefinedRule(rule)}
                title={`Use "${rule.label}" template`}
              >
                <span
                  className={styles.predefinedCategory}
                  style={{ backgroundColor: CATEGORY_COLORS[rule.category] || '#718096' }}
                >
                  {rule.category}
                </span>
                <div className={styles.predefinedLabel}>{rule.label}</div>
                <div className={styles.predefinedDesc}>{rule.description}</div>
              </div>
            ))}
          </div>
        </Modal.Body>
        </div>
      </Modal>

      <Card className={styles.formCard}>
        <Card.Body>
          <Form noValidate onSubmit={handleSubmit}>
            <div className={styles.formTopBar}>
              <small className="text-muted">
                Fields marked with <span className="text-danger">*</span> are required
              </small>
              <Button
                variant="outline-primary"
                size="sm"
                type="button"
                className={styles.templateBtn}
                onClick={() => setShowTemplates(true)}
              >
                Use Predefined Rule
              </Button>
            </div>

            {/* Basic Information */}
            <div className={styles.sectionContainer}>
              <div className={styles.sectionTitle}>Basic Information</div>

              <div className={styles.fieldRow}>
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    Rule Name <span className="text-danger">*</span>
                  </label>
                  {fromTemplate && state.name === PREDEFINED_RULE_DEFAULT_NAME && (
                    <div className={styles.templateDescWarning}>
                      <strong>Update the rule name. </strong>This template uses a placeholder. Replace it with a unique name before saving.
                    </div>
                  )}
                  <Form.Control
                    type="text"
                    name="name"
                    value={state.name}
                    onChange={handleInputChange}
                    onFocus={handleTemplateFocus}
                    placeholder="e.g. High Value 2x Bonus"
                    isInvalid={!!state.fieldErrors?.name}
                    className={fromTemplate && state.name === PREDEFINED_RULE_DEFAULT_NAME ? styles.descriptionWarningInput : ''}
                  />
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {state.fieldErrors?.name}
                  </Form.Control.Feedback>
                </div>

                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    Priority <span className="text-danger">*</span>
                  </label>
                  <Form.Control
                    type="number"
                    name="priority"
                    value={state.priority}
                    onChange={handleInputChange}
                    placeholder="1 = highest priority"
                    min="1"
                    step="1"
                    isInvalid={!!state.fieldErrors?.priority}
                  />
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {state.fieldErrors?.priority}
                  </Form.Control.Feedback>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Description</label>
                {fromTemplate && state.description === PREDEFINED_RULE_DEFAULT_DESCRIPTION && (
                  <div className={styles.templateDescWarning}>
                    <strong>Update the description. </strong>This template uses a placeholder. Replace it with a description that reflects your specific rule before saving.
                  </div>
                )}
                <Form.Control
                  as="textarea"
                  name="description"
                  value={state.description}
                  onChange={handleInputChange}
                  onFocus={handleTemplateFocus}
                  placeholder="Optional description of this rule"
                  rows={2}
                  className={fromTemplate && state.description === PREDEFINED_RULE_DEFAULT_DESCRIPTION ? styles.descriptionWarningInput : ''}
                />
              </div>

              <div className={styles.activeCheckRow}>
                <Form.Check
                  type="switch"
                  id="isActiveSwitch"
                  name="isActive"
                  checked={state.isActive}
                  onChange={handleInputChange}
                />
                <span className={styles.activeCheckLabel}>
                  Rule is {state.isActive ? <strong>active</strong> : <span className="text-muted">inactive</span>}
                </span>
              </div>
            </div>

            {/* Date Range */}
            <div className={styles.sectionContainer}>
              <div className={styles.sectionTitle}>
                Date Range <span className={styles.optionalBadge}>Optional</span>
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>Start Date</label>
                  <Form.Control
                    type="datetime-local"
                    name="startDate"
                    value={state.startDate}
                    onChange={handleInputChange}
                  />
                </div>

                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>End Date</label>
                  <Form.Control
                    type="datetime-local"
                    name="endDate"
                    value={state.endDate}
                    onChange={handleInputChange}
                    isInvalid={!!state.fieldErrors?.dateRange}
                  />
                  {state.fieldErrors?.dateRange && (
                    <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                      {state.fieldErrors?.dateRange}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Condition Operator */}
            <div className={styles.sectionContainer}>
              <div className={styles.sectionTitle}>Condition Operator</div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>
                  Operator <span className="text-danger">*</span>
                </label>
                <div className={styles.operatorToggleGroup}>
                  {CONDITION_OPERATORS.map((op) => (
                    <button
                      key={op.id}
                      type="button"
                      className={`${styles.operatorBtn} ${state.conditionOperator === op.id ? styles.operatorBtnActive : ''}`}
                      onClick={() =>
                        setState({ conditionOperator: op.id, fieldErrors: { ...state.fieldErrors, conditionOperator: '' } })
                      }
                    >
                      <strong>{op.id}</strong>&nbsp;&nbsp;{op.id === 'AND' ? 'All conditions must match' : 'Any condition must match'}
                    </button>
                  ))}
                </div>
                {state.fieldErrors?.conditionOperator && (
                  <div className="text-danger" style={{ fontSize: '12px', marginTop: '6px' }}>
                    {state.fieldErrors?.conditionOperator}
                  </div>
                )}
                <div className={styles.fieldHint}>
                  {state.conditionOperator === 'AND'
                    ? 'The rule applies only when all conditions below are satisfied.'
                    : 'The rule applies when at least one condition below is satisfied.'}
                </div>
              </div>
            </div>

            {/* Conditions */}
            <div className={styles.sectionContainer}>
              <div className={styles.sectionTitle}>Conditions</div>

              {state.conditions.map((cond, idx) => {
                const condTypeDef = CONDITION_TYPES.find((c) => c.id === cond.conditionType);
                const condErr = (state.conditionErrors || [])[idx] || {};
                return (
                  <div key={idx} className={styles.conditionCard}>
                    <div className={styles.conditionHeader}>
                      <span className={styles.conditionBadge}>{idx + 1}</span>
                      <span className={styles.conditionLabel}>Condition {idx + 1}</span>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        type="button"
                        onClick={() => handleRemoveCondition(idx)}
                        disabled={state.conditions.length === 1}
                      >
                        Remove
                      </Button>
                    </div>

                    <div className={styles.conditionBody}>
                      <div className={styles.conditionField} style={{ minWidth: '240px', flex: '2' }}>
                        <label>
                          Condition Type <span className="text-danger">*</span>
                        </label>
                        <Form.Control
                          as="select"
                          className="form-select"
                          value={cond.conditionType}
                          onChange={(e) => handleConditionChange(idx, 'conditionType', e.target.value)}
                          isInvalid={!!condErr.conditionType}
                        >
                          <option value="">Select condition type</option>
                          {CONDITION_TYPES.map((ct) => (
                            <option key={ct.id} value={ct.id}>
                              {ct.name}
                            </option>
                          ))}
                        </Form.Control>
                        {condErr.conditionType && (
                          <div className="text-danger" style={{ fontSize: '12px' }}>
                            {condErr.conditionType}
                          </div>
                        )}
                      </div>

                      <div className={styles.conditionField} style={{ flex: '1' }}>
                        <label>
                          {condTypeDef?.needsSecondary ? 'From Value' : 'Value'}{' '}
                          <span className="text-danger">*</span>
                        </label>
                        <Form.Control
                          type="number"
                          placeholder="e.g. 100"
                          value={cond.value}
                          onChange={(e) => handleConditionChange(idx, 'value', e.target.value)}
                          isInvalid={!!condErr.value}
                          step="any"
                        />
                        {condErr.value && (
                          <div className="text-danger" style={{ fontSize: '12px' }}>
                            {condErr.value}
                          </div>
                        )}
                      </div>

                      {condTypeDef?.needsSecondary && (
                        <>
                          <div className={styles.conditionSeparator}>to</div>
                          <div className={styles.conditionField} style={{ flex: '1' }}>
                            <label>
                              Max Value <span className="text-danger">*</span>
                            </label>
                            <Form.Control
                              type="number"
                              placeholder="e.g. 500"
                              value={cond.secondaryValue}
                              onChange={(e) => handleConditionChange(idx, 'secondaryValue', e.target.value)}
                              isInvalid={!!condErr.secondaryValue}
                              step="any"
                            />
                            {condErr.secondaryValue && (
                              <div className="text-danger" style={{ fontSize: '12px' }}>
                                {condErr.secondaryValue}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

              <Button
                variant="outline-primary"
                size="sm"
                type="button"
                onClick={handleAddCondition}
                className={`mt-1 ${styles.addConditionBtn}`}
              >
                + Add Condition
              </Button>
            </div>

            {/* Action */}
            <div className={styles.sectionContainer}>
              <div className={styles.sectionTitle}>Action</div>

              <div className={styles.fieldRow}>
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    Action Type <span className="text-danger">*</span>
                  </label>
                  <Form.Control
                    as="select"
                    className="form-select"
                    name="actionType"
                    value={state.actionType}
                    onChange={handleInputChange}
                    isInvalid={!!state.fieldErrors?.actionType}
                  >
                    <option value="">Select action type</option>
                    {ACTION_TYPES.map((at) => (
                      <option key={at.id} value={at.id}>
                        {at.name}
                      </option>
                    ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {state.fieldErrors?.actionType}
                  </Form.Control.Feedback>
                </div>

                {actionTypeDef?.needsValue && (
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>
                      Action Value <span className="text-danger">*</span>
                    </label>
                    <Form.Control
                      type="number"
                      name="actionValue"
                      value={state.actionValue}
                      onChange={handleInputChange}
                      placeholder="e.g. 2.0"
                      step="any"
                      min="0"
                      isInvalid={!!state.fieldErrors?.actionValue}
                    />
                    <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                      {state.fieldErrors?.actionValue}
                    </Form.Control.Feedback>
                  </div>
                )}
              </div>
            </div>

            {/* MCC Scope */}
            <div className={styles.sectionContainer}>
              <div className={styles.sectionTitle}>
                MCC Scope <span className={styles.optionalBadge}>Optional</span>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>MCC Codes</label>
                <Form.Control
                  type="text"
                  name="mccCodes"
                  value={state.mccCodes}
                  onChange={handleInputChange}
                  placeholder="e.g. 1111, 2222"
                />
                <div className={styles.fieldHint}>Leave empty to apply to all MCC codes.</div>
              </div>
            </div>

            {/* Tier Scope */}
            <div className={styles.sectionContainer}>
              <div className={styles.sectionTitle}>
                Tier Scope <span className={styles.optionalBadge}>Optional</span>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Loyalty Tiers</label>
                {state.tiersLoading ? (
                  <span className="text-muted">Loading tiers...</span>
                ) : state.tiers.length === 0 ? (
                  <span className="text-muted">No tiers available.</span>
                ) : (
                  <div className={styles.tierCheckboxGroup}>
                    {state.tiers.map((tier) => (
                      <label
                        key={tier.id}
                        className={state.selectedTierIds.includes(tier.id) ? styles.tierCheckboxSelected : ''}
                      >
                        <input
                          type="checkbox"
                          checked={state.selectedTierIds.includes(tier.id)}
                          onChange={() => handleTierToggle(tier.id)}
                        />
                        {tier.name}
                      </label>
                    ))}
                  </div>
                )}
                <div className={styles.fieldHint}>Leave unchecked to apply to all tiers.</div>
              </div>
            </div>

            <div className={styles.formActions}>
              <Button variant="secondary" type="button" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={state.loading}>
                {state.loading ? 'Creating...' : 'Create Rule'}
              </Button>
            </div>
          </Form>

          {state.loading && <Loading />}
        </Card.Body>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({ auth: state.auth });
export default connect(mapStateToProps)(AddRewardRule);
